import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Award, Clock, Heart, Users } from 'lucide-react';
import { supabase, isSupabaseConfigured } from '../admin/lib/supabaseClient';
import type { CarListing } from '../admin/types';

const About: React.FC = () => {
  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.5 }
  };

  const staggerChildren = { animate: { transition: { staggerChildren: 0.06 } } };

  const features = [
    { icon: Award, title: '30+ Years Experience', desc: 'Decades of specialized autobody work' },
    { icon: Clock, title: '250+ Restorations', desc: 'Minis restored to perfection' },
    { icon: Heart, title: 'Passion Driven', desc: 'Love for classic automobiles' },
    { icon: Users, title: 'Family Business', desc: 'Personal service & attention' },
  ];

  return (
    <section id="about" className="relative py-12 bg-white">
      <div className="container mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">

        <motion.div variants={fadeInUp} initial="initial" animate="animate">
          <h2 className="text-3xl font-bold mb-4">About Gerrie's Classic Minis</h2>
          <p className="text-gray-700 dark:text-gray-300 mb-4">
            Gerrie Everts is a highly regarded classic Mini expert, collector, and restorer based in Pretoria, South Africa. With over three decades of experience, he specializes in the preservation and unique modification of these iconic British cars.
          </p>
          <p className="text-gray-700 dark:text-gray-300 mb-4">
            As a master restorer, Gerrie possesses exceptional skills in automotive body repair and mechanics, enabling him to rescue and rebuild Minis that others would consider beyond repair. He is renowned for constructing highly customized touring Minis designed for comfort and reliability on long-distance journeys across Southern Africa.
          </p>
          <p className="text-gray-700 dark:text-gray-300 mb-6">
            Together with his wife, Theresa, Gerrie has amassed an extensive collection of classic Mini models over many years, including rare South African-produced versions such as all three Cooper and 'S' models, a Wolseley 1000, and a Mini Marcos. Their vision is to eventually establish a dedicated Mini museum to showcase these treasures.
          </p>

          <motion.div className="grid sm:grid-cols-2 gap-4 mb-6" variants={staggerChildren}>
            {features.map((feature, i) => (
              <motion.div key={i} className="flex items-start space-x-3 p-2" variants={fadeInUp}>
                <div className="flex-shrink-0 w-10 h-10 bg-mini-red/10 rounded-lg flex items-center justify-center">
                  <feature.icon size={18} className="text-mini-red" />
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 dark:text-white text-sm">{feature.title}</h4>
                  <p className="text-gray-600 dark:text-gray-400 text-sm">{feature.desc}</p>
                </div>
              </motion.div>
            ))}
          </motion.div>

          <motion.div className="bg-mini-cream dark:bg-mini-green/20 border-l-4 border-mini-green p-6 rounded-r-lg mb-6" variants={fadeInUp}>
            <h3 className="font-bold text-gray-900 dark:text-white mb-2">Gemini Classic Garage</h3>
            <p className="text-gray-700 dark:text-gray-300 text-sm mb-2">
              The workshop formerly operated by Gerrie Everts is now known as <strong>Gemini Classic Garage</strong>, located in Silverton, Pretoria, and managed by Mike Odendaal. The garage continues the tradition of excellence, offering specialized services for classic Minis including servicing, refurbishments, rebuilds, and rare spares.
            </p>
            <p className="text-gray-600 dark:text-gray-400 text-sm">
              Visit: <a href="https://classicminis.co.za" target="_blank" rel="noopener noreferrer" className="text-mini-red hover:underline font-medium">classicminis.co.za</a>
            </p>
          </motion.div>

          <motion.div className="bg-mini-green/10 dark:bg-mini-green/20 border-l-4 border-mini-green p-6 rounded-r-lg" variants={fadeInUp}>
            <p className="text-mini-green font-semibold text-lg italic">
              "Every Mini has a story. Our job is to make sure that story continues through expert craftsmanship and restoration."
            </p>
            <p className="text-gray-600 dark:text-gray-400 text-sm mt-2">— Gerrie Everts</p>
          </motion.div>
        </motion.div>

        <motion.div variants={fadeInUp} initial="initial" animate="animate">
          <div className="bg-white rounded-2xl p-2 shadow-sm">
            <FeaturedMiniCarousel />
          </div>
        </motion.div>

      </div>
    </section>
  );
};

export default About;

function FeaturedMiniCarousel() {
  const [cars, setCars] = useState<CarListing[]>([]);
  const [index, setIndex] = useState(0);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    let sub: any = null;

    async function load() {
      if (!isSupabaseConfigured) {
        // Provide a neutral sample car so the public About page doesn't show an empty red card
        const sample = [{
          id: 'sample-1',
          make: 'Mini',
          model: 'Cooper',
          year: 1970,
          price: 2500000,
          status: 'available',
          image_url: '/background/desktop-wallpaper-mini-cooper-classic.jpg',
          color: 'British Racing Green',
          created_at: new Date().toISOString(),
        } as unknown as CarListing];
        if (mounted.current) setCars(sample);
        return;
      }
      try {
        const { data, error } = await supabase
          .from('car_listings')
          .select('id, make, model, year, price, status, image_url, images, color, created_at')
          .in('status', ['available', 'reserved', 'under-offer'])
          .order('is_featured', { ascending: false })
          .order('created_at', { ascending: false })
          .limit(10);
        if (error) throw error;
        if (!mounted.current) return;
        setCars(data || []);

        try {
          sub = supabase.channel('public:car_listings_feat')
            .on('postgres_changes', { event: '*', schema: 'public', table: 'car_listings' }, async () => {
              const { data: d } = await supabase
                .from('car_listings')
                .select('id, make, model, year, price, status, image_url, images, color, created_at')
                .in('status', ['available', 'reserved', 'under-offer'])
                .order('is_featured', { ascending: false })
                .order('created_at', { ascending: false })
                .limit(10);
              if (!mounted.current) return;
              setCars(d || []);
            })
            .subscribe();
        } catch (e) {
          // ignore subscribe errors
        }
      } catch (e) {
        console.warn('Failed to load featured minis', e);
      }
    }

    load();

    return () => {
      mounted.current = false;
      try { if (sub && typeof sub.unsubscribe === 'function') sub.unsubscribe(); } catch (e) {}
    };
  }, []);

  useEffect(() => {
    if (!cars || cars.length === 0) return;
    setIndex(0);
    const t = setInterval(() => setIndex(i => (i + 1) % cars.length), 4500);
    return () => clearInterval(t);
  }, [cars]);

  const car = cars.length ? cars[index] : null;

  if (!car) {
    // If somehow no car is available, show a neutral placeholder with an image (not a red gradient)
    return (
      <div className="relative overflow-hidden rounded-2xl shadow-2xl">
        <div className="aspect-[4/3] bg-white flex items-center justify-center">
          <img
            src="/background/desktop-wallpaper-mini-cooper-classic.jpg"
            alt="Sample Mini"
            className="w-full h-full object-cover rounded-2xl"
            fetchPriority="high"
            decoding="async"
          />
        </div>
        <div className="absolute inset-0 p-6 flex flex-col justify-end text-gray-900">
          <div className="inline-block px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800">AVAILABLE</div>
          <h3 className="text-xl font-bold mt-3">Mini Cooper</h3>
          <p className="text-gray-600">1970 • British Racing Green</p>
          <div className="mt-3 flex items-center gap-3">
            <a href="/mini-sales" className="bg-mini-red text-white px-4 py-2 rounded-lg font-semibold">View Listings</a>
            <div className="font-bold text-lg">R 25,000.00</div>
          </div>
        </div>
      </div>
    );
  }

  const formatPrice = (p?: number) => p ? `R ${(p / 100).toLocaleString()}` : 'Price N/A';

  return (
    <div className="relative overflow-hidden rounded-2xl shadow-2xl">
      <div className="aspect-[4/3] bg-white">
        {car.image_url ? (
          <img
            src={car.image_url}
            alt={`${car.make} ${car.model}`}
            className="w-full h-full object-cover"
            loading={car.image_url ? 'eager' : undefined}
            decoding="async"
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-mini-red/10 to-mini-green/10 flex items-center justify-center text-gray-900">
            <div className="text-center">
              <p className="text-lg font-semibold">{car.make} {car.model}</p>
              <p className="text-sm opacity-90">{car.year} Classic</p>
            </div>
          </div>
        )}
      </div>

      <div className={`absolute inset-0 p-6 flex flex-col justify-end ${car.image_url ? 'text-white' : 'text-gray-900'}`}>
        <div className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${car.status === 'available' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>{car.status.replace('-', ' ').toUpperCase()}</div>
        <h3 className={`text-xl font-bold mt-3 ${car.image_url ? 'text-white' : 'text-gray-900'}`}>{car.make} {car.model}</h3>
        <p className={`${car.image_url ? 'text-white/90' : 'text-gray-600'}`}>{car.year} • {car.color || ''}</p>
        <div className="mt-3 flex items-center gap-3">
          <a href={`/mini-sales#${car.id}`} target="_blank" rel="noopener noreferrer" className={`${car.image_url ? 'bg-white text-mini-red' : 'bg-mini-red text-white'} px-4 py-2 rounded-lg font-semibold`}>View Listing</a>
          <div className={`${car.image_url ? 'text-white' : 'text-gray-900'} font-bold text-lg`}>{formatPrice(car.price)}</div>
        </div>
      </div>
    </div>
  );
}
